/* path: src/customizations/components/theme/View/NewsItemView.jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import { RenderBlocks } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

function formatDate(value, locale = 'en-US') {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  }).format(d);
}

function joinUrl(base, rel) {
  if (!base || !rel) return null;
  const b = String(base).replace(/\/+$/, '');
  const r = String(rel).replace(/^\/+/, '');
  return `${b}/${r}`;
}

function pickBestDownloadFromScales(scales) {
  // prefer bigger known scales first; fall back to anything available
  const order = ['huge', 'great', 'larger', 'large', 'teaser', 'preview', 'mini', 'tile', 'thumb', 'icon'];
  for (const key of order) {
    const hit = scales?.[key]?.download;
    if (hit) return hit;
  }
  return null;
}

function getPreviewImageUrlFromLink(previewImageLink) {
  const baseId = previewImageLink?.['@id'];
  const imageArr = previewImageLink?.image_scales?.image;

  // plone.restapi sometimes returns image_scales.image as an array of one item
  const img0 = Array.isArray(imageArr) ? imageArr[0] : null;

  const bestScale = pickBestDownloadFromScales(img0?.scales);
  const fallbackOriginal = img0?.download;

  const rel = bestScale || fallbackOriginal || null;
  const raw = rel ? joinUrl(baseId, rel) : null;

  return raw ? flattenToAppURL(raw) : null;
}

function getImageUrl(content) {
  // 1) Your actual preview image data lives here (per your JSON)
  const fromPreviewLink = getPreviewImageUrlFromLink(content?.preview_image_link);
  if (fromPreviewLink) return fromPreviewLink;

  // 2) fallback if you ever add a real lead image field later
  const imageArr = content?.image_scales?.image;
  const img0 = Array.isArray(imageArr) ? imageArr[0] : null;
  const bestScale = pickBestDownloadFromScales(img0?.scales);
  const fallbackOriginal = img0?.download;
  const rel = bestScale || fallbackOriginal || null;

  // content['@id'] is the News Item url
  const raw = rel ? joinUrl(content?.['@id'], rel) : null;
  return raw ? flattenToAppURL(raw) : null;
}

export default function NewsItemView(props) {
  const { content, intl } = props;
  const locale = intl?.locale || 'en-US';

  const effective = formatDate(content?.effective, locale);
  const description = content?.description || '';

  const imageUrl = getImageUrl(content);

  return (
    <div id="page-document" className="ui container view-wrapper newsitem-view">
      <Container>
        {/* IMPORTANT: RenderBlocks FIRST so the generated Title block (documentFirstHeading) stays on top */}
        <RenderBlocks {...props} />

        {/* Everything below will appear BELOW documentFirstHeading */}
        <section className="newsitem-body">
          {effective ? <div className="newsitem-date">{effective}</div> : null}

          {description ? (
            <div className="newsitem-lead">
              <p>{description}</p>
            </div>
          ) : null}

          {imageUrl ? (
            <figure className="newsitem-image">
              <img src={imageUrl} alt="" loading="lazy" />
            </figure>
          ) : null}
        </section>
      </Container>
    </div>
  );
}

NewsItemView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    effective: PropTypes.string,
    description: PropTypes.string,
    blocks: PropTypes.object,
    blocks_layout: PropTypes.object,

    // your actual preview image relation field
    preview_image_link: PropTypes.shape({
      '@id': PropTypes.string,
      image_scales: PropTypes.object,
    }),

    // optional fallback
    image_scales: PropTypes.object,
  }).isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string,
  }),
};